import { ConfigProvider, Drawer, Segmented } from 'antd';
import styled from 'styled-components';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Button, Col, Colors, InputSearch, Row, Text } from 'basic-ui-lib';
import useGetAllScreens from 'modules/playlist/services/hooks/useGetAllScreen';
import { IScreen } from 'modules/dashboard/interfaces/api';
import SetToScreenMobileItem from './SetToScreenMobileItem';
import { setPageLoading } from 'widgets/PageLoading';
import playlistApis from 'modules/playlist/services/apis';
import toast from 'utils/toast';

export interface SetToScreenMobileRef {
  open: (playlistId: string) => void;
}

interface SetToScreenMobileProps {}

const SetToScreenMobile = forwardRef<SetToScreenMobileRef, SetToScreenMobileProps>(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [playlistId, setPlaylistId] = useState('');
  const [type, setType] = useState<string>('ALL');
  const [screens] = useGetAllScreens(open);
  const [searchValue, setSearchValue] = useState('');
  const [listScreens, setListScreens] = useState<string[]>([]);

  useImperativeHandle(ref, () => ({
    open: (playlistId: string) => {
      setPlaylistId(playlistId);
      setOpen(true);
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setListScreens([]);
  };

  const filteredScreens: IScreen[] = useMemo(() => {
    let result = screens;

    if (type !== 'ALL') {
      result = result.filter((screen) => (type === 'ONLINE' ? screen.status === 'ONLINE' : screen.status !== 'ONLINE'));
    }

    if (searchValue.trim()) {
      const keyword = searchValue.toLowerCase();
      result = result.filter(
        (screen) =>
          screen?.playerName?.toLowerCase().includes(keyword) || screen?.businessName?.toLowerCase().includes(keyword),
      );
    }

    return result;
  }, [screens, type, searchValue]);

  const handleChooseScreen = (screenId: string, checked: boolean) => {
    if (checked) {
      setListScreens((prev) => [...prev, screenId]);
    } else {
      setListScreens((prev) => prev.filter((screen) => screen !== screenId));
    }
  };
  const handleOk = async () => {
    try {
      setPageLoading(true);
      const res = await playlistApis.setScreen(playlistId, listScreens);

      if (res?.data?.data) {
        toast.success('Set to screen successfully');
        handleClose();
      }
    } catch (error) {
      toast.error('Failed to set to screen');
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <ConfigProvider>
      <StyledDrawer
        title={
          <DrawerHeader>
            <Text title_h6 semibold center>
              Set to Screen
            </Text>
          </DrawerHeader>
        }
        placement='bottom'
        closable={false}
        onClose={handleClose}
        open={open}
        height='90%'
        footer={
          <Row>
            <Button type_cancel lg onClick={handleClose} style={{ width: '100%' }}>
              Close
            </Button>
            <Button type_second lg style={{ width: '100%' }} onClick={handleOk}>
              OK
            </Button>
          </Row>
        }
        destroyOnClose
        style={{ borderRadius: '10px 10px 0px 0px' }}
      >
        <InputSearch value={searchValue} onChange={setSearchValue} placeholder='Search store name/phone' />
        <Segmented options={['ALL', 'ONLINE', 'OFFLINE']} defaultValue='ALL' onChange={setType} />
        <Col>
          {filteredScreens.map((screen) => (
            <SetToScreenMobileItem
              key={screen.id}
              screen={screen}
              checked={listScreens.includes(screen.id)}
              onChange={(checked) => handleChooseScreen(screen.id, checked)}
            />
          ))}
        </Col>
      </StyledDrawer>
    </ConfigProvider>
  );
});

SetToScreenMobile.displayName = 'SetToScreenMobile';
export default SetToScreenMobile;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 16px 16px 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    background-color: ${Colors.neutral.bg[2]};
  }

  .ant-drawer-footer {
    padding: 16px 16px;
    box-shadow: 0px -4px 8px 0px #0000001a;
    background-color: ${Colors.neutral.bg[1]};
  }

  .ant-radio-group {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .ant-segmented-item {
    flex: 1;
    padding: 8px 0;
  }

  .ant-segmented-item-label {
    font-size: 16px;
    font-weight: 600;
  }

  .ant-segmented-item-selected {
    border-radius: 12px;
    color: ${Colors.primary[6]};
  }
`;

const DrawerHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
